import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, TrendingUp, TrendingDown, Minus, Medal } from "lucide-react";

const NAVY = "#0B1B3F";

const MEDAL = {
  1: "bg-amber-400 text-white",
  2: "bg-slate-300 text-slate-700",
  3: "bg-amber-700 text-white",
};

export default function ExamRanking() {
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/analytics/exam-ranking/${period}`);
      setData(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const trendOf = (r) => {
    if (r.diff == null) return { label: "Veri yok", cls: "bg-slate-100 text-slate-500 border-slate-200", Icon: Minus };
    if (r.diff > 0.5) return { label: `+${r.diff} yükseldi`, cls: "bg-emerald-100 text-emerald-700 border-emerald-300", Icon: TrendingUp };
    if (r.diff < -0.5) return { label: `${r.diff} düştü`, cls: "bg-rose-100 text-rose-700 border-rose-300", Icon: TrendingDown };
    return { label: "Sabit", cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: Minus };
  };

  return (
    <div className="space-y-5" data-testid="exam-ranking">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <Medal className="text-amber-500" size={26} /> Kurum Deneme Başarı Tablosu
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Öğrenci bazlı deneme kıyaslaması — önceki dönemle karşılaştırmalı.
            {data && (
              <span className="font-medium text-slate-700">
                {" "}{data.start} → {data.end} (önceki: {data.prev_start} → {data.prev_end})
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-1.5 rounded-xl border border-slate-200 bg-white p-1">
          {[
            { id: "week", label: "Bu Hafta" },
            { id: "month", label: "Bu Ay" },
          ].map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              data-testid={`period-${p.id}`}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                period === p.id ? "bg-green-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
        <div className="text-white px-5 py-3.5 flex items-center justify-between" style={{ background: NAVY }}>
          <span className="font-display font-extrabold tracking-wide">KURUM SIRALAMASI — {period === "week" ? "HAFTALIK" : "AYLIK"}</span>
          <span className="text-xs font-bold bg-green-500 text-white px-3 py-1 rounded-full">{data?.rows?.length || 0} öğrenci</span>
        </div>
        <div className="overflow-x-auto bg-white">
          <table className="w-full text-sm min-w-[950px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5 font-medium w-16">Sıra</th>
                <th className="px-4 py-2.5 font-medium">Öğrenci</th>
                <th className="px-4 py-2.5 font-medium">Alan</th>
                <th className="px-4 py-2.5 font-medium text-right">Genel Deneme</th>
                <th className="px-4 py-2.5 font-medium text-right">Branş Deneme</th>
                <th className="px-4 py-2.5 font-medium text-right">TYT Ort.</th>
                <th className="px-4 py-2.5 font-medium text-right">AYT Ort.</th>
                <th className="px-4 py-2.5 font-medium text-right">Ortalama Net</th>
                <th className="px-4 py-2.5 font-medium text-right">Önceki Dönem</th>
                <th className="px-4 py-2.5 font-medium">Dönem Kıyası</th>
              </tr>
            </thead>
            <tbody data-testid="exam-ranking-table">
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={20} /></td></tr>
              ) : !data || data.rows.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">Öğrenci kaydı yok.</td></tr>
              ) : (
                data.rows.map((r) => {
                  const tr = trendOf(r);
                  return (
                    <tr key={r.student_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`examrank-row-${r.student_id}`}>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-display font-extrabold text-sm ${MEDAL[r.rank] || "bg-slate-100 text-slate-500"}`} data-testid={`examrank-${r.rank}`}>
                          {r.rank}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">{r.name}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{r.track || "-"}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{r.exam_count}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{r.branch_count}</td>
                      <td className="px-4 py-3 text-right font-mono text-blue-700">{r.tyt_avg ?? "-"}</td>
                      <td className="px-4 py-3 text-right font-mono text-amber-600">{r.ayt_avg ?? "-"}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-slate-900">{r.avg_net.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-mono text-slate-500">{r.prev_avg_net ?? "-"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${tr.cls}`} data-testid={`examrank-trend-${r.student_id}`}>
                          <tr.Icon size={13} /> {tr.label}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
